import { masterDetailsMinHeight, pageBgColor } from "@/utils/theme";
import {
	ErrorModal,
	HelmetDexteel,
	getMomentTz,
	useUserContext,
} from "@dexteel/mesf-core";
import { Divider, Grid2, Typography } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { Moment } from "moment-timezone";
import { useEffect, useState } from "react";
import { DelaysParetoGraphic } from "./components/DelaysParetoGraphic";
import { DrillDownModal } from "./components/DrillDownModal";
import { Filters } from "./components/filters/Filters";
import { DelayForPareto } from "./models/DelayForPareto";
import { getDelaysForPareto } from "./repositories/DelaysParetoReportRepository";

const moment = getMomentTz();

type useDelaysParetoProps = {
	startDate: Date;
	endDate: Date;
	delayAreaAssetId?: number | null;
	includeMicroDelays: boolean;
	includeUndeclared: boolean;
	includeRollChange: boolean;
	onError: (error: Error) => void;
};

export const useDelaysPareto = ({
	startDate,
	endDate,
	delayAreaAssetId,
	includeMicroDelays,
	includeUndeclared,
	includeRollChange,
	onError,
}: useDelaysParetoProps) => {
	return useQuery<DelayForPareto[], Error>({
		queryKey: [
			"delaysPareto",
			startDate,
			endDate,
			delayAreaAssetId,
			includeMicroDelays,
			includeUndeclared,
			includeRollChange,
		],
		queryFn: ({ signal }) =>
			getDelaysForPareto(
				{
					startDate,
					endDate,
					delayAreaAssetId,
					includeMicroDelays,
					includeUndeclared,
					includeRollChange,
				},
				signal,
			),
		onError: onError,
	});
};

export const DelaysParetoReportPage = () => {
	const {
		state: { defaultAreaId },
	} = useUserContext();
	const [error, setError] = useState("");
	const [includeMicrodelays, setIncludeMicrodelays] = useState<boolean>(true);
	const [includeUndeclaredDelays, setIncludeUndeclaredDelays] =
		useState<boolean>(true);
	const [includeRollChange, setIncludeRollChange] = useState<boolean>(true);

	const [filterStartDate, setFilterStartDate] = useState<Moment>(
		moment().subtract(7, "days").startOf("day"),
	);
	const [filterEndDate, setFilterEndDate] = useState<Moment>(
		moment().endOf("day"),
	);
	const [selectedDelayArea, setSelectedDelayArea] = useState<number | null>(
		Number(defaultAreaId) || null,
	);

	useEffect(() => {
		if (defaultAreaId && selectedDelayArea === null) {
			setSelectedDelayArea(Number(defaultAreaId) || null);
		}
	}, [defaultAreaId]);

	const [drillDownItem, setDrillDownItem] = useState<DelayForPareto | null>(
		null,
	);

	const { data: delaysPareto } = useDelaysPareto({
		startDate: filterStartDate.toDate(),
		endDate: filterEndDate.toDate(),
		delayAreaAssetId: selectedDelayArea,
		includeMicroDelays: includeMicrodelays,
		includeUndeclared: includeUndeclaredDelays,
		includeRollChange,
		onError: (e) => setError(e.message),
	});

	const handleFilterChange = (startDate: Moment, endDate: Moment) => {
		setFilterStartDate(startDate);
		setFilterEndDate(endDate);
	};

	const handleDelayAreaChange = (assetId: number | null) => {
		setSelectedDelayArea(assetId);
	};

	const handleIncludeMicrodelaysChange = (include: boolean) => {
		setIncludeMicrodelays(include);
	};

	const handleIncludeUndeclaredDelaysChange = (include: boolean) => {
		setIncludeUndeclaredDelays(include);
	};

	const handleIncludeRollChangeChange = (include: boolean) => {
		setIncludeRollChange(include);
	};

	return (
		<>
			<HelmetDexteel title="Pareto Report" />
			<Grid2
				container
				size={{ md: 12, xs: 12 }}
				spacing={1}
				px={2}
				pt={2}
				sx={{ backgroundColor: pageBgColor, minHeight: "calc(100vh - 200px)" }}
			>
				<Grid2 size={{ md: 12, xs: 12 }}>
					<Typography variant="h5" style={{ fontWeight: 600 }}>
						Pareto Report
					</Typography>
				</Grid2>
				<Grid2 size={{ md: 12 }}>
					<Filters
						onFilterChange={handleFilterChange}
						initialStartDate={filterStartDate}
						initialEndDate={filterEndDate}
						selectedDelayArea={selectedDelayArea}
						onDelayAreaChange={handleDelayAreaChange}
						includeMicrodelays={includeMicrodelays}
						onIncludeMicrodelaysChange={handleIncludeMicrodelaysChange}
						includeUndeclaredDelays={includeUndeclaredDelays}
						onIncludeUndeclaredDelaysChange={
							handleIncludeUndeclaredDelaysChange
						}
						includeRollChange={includeRollChange}
						onIncludeRollChangeChange={handleIncludeRollChangeChange}
					/>
				</Grid2>

				<Grid2 size={{ md: 12 }}>
					<Divider />
				</Grid2>

				<Grid2 size={{ md: 12 }}>
					<DelaysParetoGraphic
						delaysForPareto={delaysPareto || []}
						onBarClick={(item) => setDrillDownItem(item)}
					/>
				</Grid2>
			</Grid2>
			<DrillDownModal
				item={drillDownItem}
				startDate={filterStartDate.toDate()}
				endDate={filterEndDate.toDate()}
				delayAreaAssetId={selectedDelayArea}
				includeMicroDelays={includeMicrodelays}
				onClose={() => setDrillDownItem(null)}
			/>
			<ErrorModal
				error={error}
				onHide={() => {
					setError("");
				}}
			/>
		</>
	);
};
